import { track } from "@connect/observability";
import type { PostMessageClient } from "@connect/post-message/client";
import type { ConnectMessageMap } from "@connect/post-message/protocol";
import { flushTerminal } from "../../lib/flush-terminal.ts";
import {
  type ActiveConnection,
  type Connection,
  type ConfigValidationFieldError,
  ConnectionErrorCode,
  type ConnectionError,
  type ConnectionRequired,
  ConnectionStatus,
  type PendingConnection,
  type TermsAcceptanceRequiredConnection,
  type UnavailableConnection,
} from "@moonpay/platform-protocol/models/connection";
import type {
  ActiveMessageArgs,
  ConnectionRequiredMessageArgs,
  MessageMeta,
  PendingMessageArgs,
} from "./external-messages.types.ts";

/**
 * Every external (frame -> parent) message the check-connection frame can send.
 * Each method builds a payload typed against the post-message protocol (the
 * source of truth for what partners receive) and emits the observability event
 * alongside the send, so the curated event and the wire message stay in lockstep.
 *
 *   const out = createExternalMessages(pm.send);
 *   out.active({ credentials, capabilities, customerId, meta: { durationMs } });
 *   out.connectionFailedGeneric("Authorization failed", { durationMs, reason });
 */
export function createExternalMessages(send: PostMessageClient<ConnectMessageMap>["send"]) {
  // This frame is headless and the embedder unmounts it on complete/error, so
  // await delivery before notifying the parent rather than racing teardown.
  const complete = async (payload: Connection, meta: MessageMeta) => {
    track.info("connect.check.connection", { connectionStatus: payload.status, ...meta });
    await flushTerminal();
    send("complete", payload);
  };

  const fail = async (payload: ConnectionError, meta: MessageMeta) => {
    track.error("connect.check.error", { code: payload.code, ...meta });
    await flushTerminal();
    send("error", payload);
  };

  return {
    active({ credentials, capabilities, customerId, meta = {} }: ActiveMessageArgs) {
      const payload: ActiveConnection = {
        status: ConnectionStatus.active,
        credentials,
        customer: { id: customerId },
        capabilities,
      };
      return complete(payload, meta);
    },

    connectionRequired({
      credentials,
      capabilities,
      mismatch,
      meta = {},
    }: ConnectionRequiredMessageArgs) {
      const payload: ConnectionRequired = {
        status: ConnectionStatus.connectionRequired,
        credentials,
        capabilities,
        ...(mismatch === true ? { mismatch: true } : {}),
      };
      return complete(payload, meta);
    },

    pending({ credentials, capabilities, customerId, meta = {} }: PendingMessageArgs) {
      const payload: PendingConnection = {
        status: ConnectionStatus.pending,
        credentials,
        customer: { id: customerId },
        capabilities,
      };
      return complete(payload, meta);
    },

    unavailable(meta: MessageMeta = {}) {
      const payload: UnavailableConnection = { status: ConnectionStatus.unavailable };
      return complete(payload, meta);
    },

    termsAcceptanceRequired(meta: MessageMeta = {}) {
      const payload: TermsAcceptanceRequiredConnection = {
        status: ConnectionStatus.termsAcceptanceRequired,
      };
      return complete(payload, meta);
    },

    validationError(errors: ConfigValidationFieldError[], meta: MessageMeta = {}) {
      const payload: ConnectionError = { code: ConnectionErrorCode.validationError, errors };
      return fail(payload, { ...meta, subCode: errors.map((e) => e.code).join(",") });
    },

    connectionFailedGeneric(message: string, meta: MessageMeta = {}) {
      const payload: ConnectionError = {
        code: ConnectionErrorCode.connectionFailedGeneric,
        message,
      };
      return fail(payload, meta);
    },
  };
}
